export type AdminView = {
  id: string;
  name: string;
  email: string;
  phone: string;
  roles: string[];
  createdAt: string;
  updatedAt: string;
};

export type AppConfigType = "boolean" | "integer" | "string" | "url";

export type AppConfigScope = "runtime" | "boot";

export type AppConfigOrigin = "registry" | "custom";

export type AdminGovernorateView = {
  id: string;
  nameAr: string;
  nameEn: string | null;
  isActive: boolean;
};

export type AdminAreaView = {
  id: string;
  governorateId: string;
  nameAr: string;
  nameEn: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
};

export type AreaSuggestionStatus = "pending" | "approved" | "rejected";

export type AdminAreaSuggestionView = {
  id: string;
  governorateId: string;
  rawName: string;
  normalizedName: string;
  status: AreaSuggestionStatus;
  resolvedAreaId: string | null;
  occurrenceCount: number;
  firstSeenAt: string;
  lastSeenAt: string;
  reviewedByAdminId: string | null;
  reviewedAt: string | null;
  createdAt: string;
  updatedAt: string;
  governorateNameAr: string;
  governorateNameEn: string | null;
};

export type ApproveAreaSuggestionResult = {
  suggestion: Omit<
    AdminAreaSuggestionView,
    "governorateNameAr" | "governorateNameEn"
  >;
  area: {
    id: string;
    governorateId: string;
    nameAr: string;
    nameEn: string | null;
    isActive: boolean;
  };
};

export type AppConfigView = {
  // Stable technical id — used for the API route and shown as a small
  // monospace subtitle in the UI. The user-facing label is `nameEn`/
  // `nameAr` selected by the active locale.
  key: string;
  value: string | null;
  // Effective default shown when no override row exists (registry default,
  // or the stored value for custom keys).
  defaultValue: string;
  nameEn: string;
  nameAr: string;
  descriptionEn: string;
  descriptionAr: string;
  type: AppConfigType;
  scope: AppConfigScope;
  origin: AppConfigOrigin;
  min?: number;
  max?: number;
  source: "db" | "unset";
  updatedAt: string | null;
};

export type AppConfigAuditView = {
  id: string;
  key: string;
  oldValue: string | null;
  // newValue is null when the row was reset (deleted) to the env default.
  newValue: string | null;
  actorId: string | null;
  actorEmail: string | null;
  changedAt: string;
};

// Feature flags — in-house, per-store targeted. Independent of AppConfig.
export type FeatureFlagStoreView = {
  id: string;
  storeName: string;
  storeSlug: string;
};

export type FeatureFlagView = {
  id: string;
  key: string;
  nameEn: string;
  nameAr: string | null;
  descriptionEn: string | null;
  descriptionAr: string | null;
  // Master kill-switch: when false the flag is off for every store.
  isActive: boolean;
  // When true the allow-list is bypassed and the flag targets all stores.
  enableForAllStores: boolean;
  createdAt: string;
  updatedAt: string;
  // The explicit allow-list (empty when enableForAllStores).
  targetStores: FeatureFlagStoreView[];
};

export type FeatureFlagAuditView = {
  id: string;
  flagKey: string;
  action: string;
  detail: string | null;
  actorId: string | null;
  actorEmail: string | null;
  changedAt: string;
};

export type ThemeStatus = "DRAFT" | "PUBLISHED" | "ARCHIVED";
// A backend-owned identifier — the catalog now carries the seeded `eo-*` family
// themes (avyro, elite, …) alongside `starter`, and it can grow again. Admin
// only DISPLAYS it, so it is typed as a string rather than a closed union that
// would silently go stale (and, worse, crash the themes list when the API
// returns a family the admin's map doesn't know — see FAMILY_LABEL_KEY usages,
// which now fall back to the raw family name). The create/edit forms still
// offer only `THEME_FAMILIES` (["starter"]) — admins don't hand-author families.
export type ThemeFamily = string;
export type ThemeVersion = {
  id: string;
  themeId: string;
  version: number;
  schemaVersion: number;
  json: Record<string, unknown>;
  thumbnailUrl: string | null;
  publishedAt: string | null;
  changelogAr: string | null;
  changelogEn: string | null;
  createdAt: string;
};
export type Theme = {
  id: string;
  family: ThemeFamily;
  slug: string;
  nameAr: string;
  nameEn: string;
  descriptionAr: string | null;
  descriptionEn: string | null;
  niches: string[];
  status: ThemeStatus;
  sortWeight: number;
  createdAt: string;
  updatedAt: string;
};
export type ThemeWithVersions = Theme & {
  versions: ThemeVersion[];
};
export type ThemeListResponse = ThemeWithVersions[];
export const THEME_FAMILIES: ThemeFamily[] = ["starter"];
export const THEME_NICHES = [
  "watches",
  "clothing",
  "electronics",
  "games",
  "digital",
  "health",
  "pets",
  "beauty",
] as const;
export type ThemeNiche = (typeof THEME_NICHES)[number];
// Publish validation errors surfaced under ApiJsonClientError.payload.errors.
export type ThemeValidationErrorItem = {
  path: string;
  code: string;
  message: string;
};

export type SellerView = {
  id: string;
  name: string;
  email: string | null;
  phone: string;
  createdAt: string;
  updatedAt: string;
};
export type SellerOptionView = {
  id: string;
  name: string;
};
export const STORE_KIND = {
  DEFAULT: "DEFAULT",
  RESTAURANT: "RESTAURANT",
  DIGITAL: "DIGITAL",
} as const;
export type StoreKind = (typeof STORE_KIND)[keyof typeof STORE_KIND];
export type StoreView = {
  id: string;
  sellerId: string;
  storeKind: StoreKind;
  storeName: string;
  storeSlug: string;
  storeShort: string;
  description: string | null;
  descriptionEn?: string | null;
  logoUrl: string | null;
  maxProductImageCount: number;
  depositPercent: number | null;
  defaultShippingFee: number | null;
  defaultDepositPercent: number | null;
  defaultIncludeShippingInRequiredAmount: boolean;
  currency: string;
  storeHighlights?: StoreHighlightView[];
  storePolicies?: StorePolicyView[];
  /** Admin deactivation (2026-10-25). Null = live. */
  deactivatedAt?: string | null;
  createdAt: string;
  updatedAt: string;
};
export type StorePolicyType = "SHIPPING" | "REFUND" | "EXCHANGE";
export type StorePolicyView = {
  id: string;
  storeId: string;
  policyType: StorePolicyType;
  isEnabled: boolean;
  titleAr: string;
  titleEn: string;
  contentAr: string;
  contentEn: string;
  createdAt: string;
  updatedAt: string;
};

export type SellerListItem = SellerView & {
  stores: StoreView[];
};
export type SellerListResponse = {
  items: SellerListItem[];
  total: number;
  limit: number;
  offset: number;
};
export type SellerOptionsResponse = {
  items: SellerOptionView[];
};
export type StoreListItem = StoreView & {
  /** Premium is the design's "Pro" plan; everything else is "Free". */
  isPremium: boolean;
  seller: SellerOptionView;
};
export type StoreOptionView = {
  id: string;
  storeName: string;
  sellerName: string;
};
export type StoreOptionsResponse = {
  items: StoreOptionView[];
};
export type StoreListResponse = {
  items: StoreListItem[];
  total: number;
  /** Catalogue-wide, so the summary does not change as you page. */
  totalAll: number;
  totalPremium: number;
  /** Deactivated stores in scope, whatever the status filter. */
  totalDeactivated?: number;
  limit: number;
  offset: number;
};
export type StoreListStatus = "active" | "deactivated" | "all";
export type StoreDeletionBlocker =
  | "ORDERS"
  | "RESTAURANT_BILLS"
  | "PAYMENTS"
  | "SUBSCRIPTION_PAYMENTS"
  | "WALLET"
  | "MONEY_LEDGER"
  | "SETTLEMENTS"
  | "CUSTOM_DOMAIN"
  | "INVENTORY"
  | "SHIFT_CASH"
  | "PAID_SERVICES"
  | "UNPAID_CHARGES"
  | "META_OBJECTS";
export type AdminStoreAuditAction =
  | "DEACTIVATE"
  | "REACTIVATE"
  | "RENAME"
  | "UPDATE"
  | "DELETE"
  | "FORCE_DELETE"
  | "PAID_LIMITS";
export type AdminStoreAuditView = {
  id: string;
  action: AdminStoreAuditAction;
  storeSlug: string;
  storeName: string;
  adminName: string | null;
  reason: string | null;
  details: Record<string, unknown>;
  createdAt: string;
};
export type StoreForceDeleteRefusal =
  | "STORE_FORCE_DELETE_REQUIRES_DEACTIVATION"
  | "STORE_FORCE_DELETE_CUSTOM_DOMAIN";
export type StoreActivityCountKey =
  | "orders"
  | "restaurantBills"
  | "billPayments"
  | "onlinePayments"
  | "moneyMovements"
  | "shifts"
  | "shiftCashMovements"
  | "driverSettlements"
  | "subscriptionPayments"
  | "storeCharges"
  | "storeCustomers"
  | "products";
export type StoreActivityCounts = Record<StoreActivityCountKey, number>;
export type AdminStoreControlsView = {
  storeId: string;
  storeSlug: string;
  storeName: string;
  storeKind: StoreKind;
  deactivation: { at: string; byName: string | null; reason: string | null } | null;
  deletion: { allowed: boolean; blockers: StoreDeletionBlocker[] };
  forceDelete: {
    allowed: boolean;
    refusals: StoreForceDeleteRefusal[];
    counts: StoreActivityCounts;
  };
  qrTableCount: number;
  hasCustomDomain: boolean;
  audits: AdminStoreAuditView[];
};
export type AdminStoreDetailsInput = {
  storeName?: string;
  storeSlug?: string;
  description?: string | null;
  descriptionEn?: string | null;
  logoObjectKey?: string | null;
};
export type AdminStoreLifecycleResult = {
  storeId: string;
  storeSlug: string;
  deactivatedAt: string | null;
  revalidated: boolean;
};
export type AdminStoreDeleteResult = {
  deleted: true;
  storeId: string;
  storeSlug: string;
  revalidated: boolean;
  followUps: string[];
};
export type AdminStoreForceDeleteResult = AdminStoreDeleteResult & {
  forced: true;
  blockers: StoreDeletionBlocker[];
};
export type StoreAdminDetailResponse = {
  store: StoreView;
  seller: {
    id: string;
  };
};
export type StoreAdminStatsResponse = {
  orders: {
    total: number;
    pending: number;
    delivered: number;
  };
  catalog: {
    products: number;
    categories: number;
  };
};
export type StoreImpersonationSessionResponse = {
  storeSlug: string;
  appHandoffCode: string;
};
export const SUBSCRIPTION_PLAN_TYPE = {
  FREE: "FREE",
  PREMIUM: "PREMIUM",
  FREE_TRIAL: "FREE_TRIAL",
  MONTHLY_SUBSCRIPTION: "MONTHLY_SUBSCRIPTION",
  /**
   * Pre-paid wallet, deducted per order. The store is never restricted for
   * running out — each uncovered order keeps its customer data masked until the
   * wallet settles the charge.
   */
  PER_ORDER_CREDIT: "PER_ORDER_CREDIT",
} as const;

export type SubscriptionPlanType =
  (typeof SUBSCRIPTION_PLAN_TYPE)[keyof typeof SUBSCRIPTION_PLAN_TYPE];

export type SubscriptionPlanView = {
  id: string;
  nameAr: string;
  nameEn: string | null;
  type: SubscriptionPlanType;
  amount: number;
  currency: string;
  /** Deduction per order. Required for PER_ORDER_CREDIT, null otherwise. */
  perOrderAmount: number | null;
  periodDays: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
};
export type SubscriptionPlanListResponse = {
  items: SubscriptionPlanView[];
};
export type StoreSubscriptionAssignmentView = {
  id: string;
  storeId: string;
  planId: string | null;
  planNameAr: string;
  planNameEn: string | null;
  type: SubscriptionPlanType;
  amount: number;
  currency: string;
  /** Per-order deduction snapshotted from the plan at assignment time. */
  perOrderAmount: number | null;
  periodDays: number;
  startDate: string;
  createdByAdmin: { id: string; name: string };
  createdAt: string;
};
export type StoreSubscriptionPaymentView = {
  id: string;
  storeId: string;
  assignmentId: string | null;
  scheduleId: string | null;
  amount: number;
  currency: string;
  paidAt: string;
  scheduleDueDate?: string | null;
  /**
   * PENDING is a seller's top-up REQUEST: no money has moved and none will
   * until an admin confirms it. CONFIRMED is the only state that corresponds
   * to wallet credit.
   */
  reviewStatus: StoreSubscriptionPaymentReviewStatus;
  /** Null while PENDING — nobody has reviewed it yet. */
  settledByAdmin: { id: string; name: string } | null;
  /** The seller who filed it. Null on an admin-recorded payment. */
  requestedBySeller: { id: string; name: string } | null;
  requestedAt: string | null;
  reviewedAt: string | null;
  /** Why an admin rejected it. Kept with the proof for audit. */
  reviewReason: string | null;
  proofObjectKey: string | null;
  proofContentType: string | null;
  proofSizeBytes: number | null;
  proofUrl?: string | null;
  /** Set once the payment was taken back out of the wallet. Never cleared. */
  reversedAt: string | null;
  reversedByAdmin: { id: string; name: string } | null;
  reversalReason: string | null;
  createdAt: string;
  allocations?: StoreSubscriptionPaymentAllocationView[];
};
export const STORE_SUBSCRIPTION_PAYMENT_REVIEW_STATUS = {
  /** A seller submitted a top-up request; no admin has acted on it. */
  PENDING: "PENDING",
  /** An admin recorded or confirmed the transfer; the wallet was credited. */
  CONFIRMED: "CONFIRMED",
  /** An admin refused it. The proof and the reason are retained for audit. */
  REJECTED: "REJECTED",
} as const;

export type StoreSubscriptionPaymentReviewStatus =
  (typeof STORE_SUBSCRIPTION_PAYMENT_REVIEW_STATUS)[keyof typeof STORE_SUBSCRIPTION_PAYMENT_REVIEW_STATUS];

/**
 * One row of the CROSS-STORE wallet top-up review queue
 * (`GET /admin/stores/wallet-top-up-requests`).
 *
 * Deliberately NOT `StoreSubscriptionPaymentView`: the queue carries the store
 * it belongs to, and it carries `hasProof` instead of the proof object key —
 * that key is a private-bucket path, and the image is still fetched through the
 * store-scoped `proof-url` route where the access is audited.
 */
export type AdminWalletTopUpRequestItem = {
  id: string;
  storeId: string;
  storeName: string;
  storeSlug: string;
  amount: number;
  currency: string;
  reviewStatus: StoreSubscriptionPaymentReviewStatus;
  requestedAt: string;
  /** Null on an admin-recorded row — that was never a seller's request. */
  requestedBySeller: { id: string; name: string } | null;
  /** Non-null only once an admin has confirmed or rejected the row. */
  reviewedAt: string | null;
  reviewReason: string | null;
  hasProof: boolean;
};
export type AdminWalletTopUpRequestListResponse = {
  items: AdminWalletTopUpRequestItem[];
  total: number;
  limit: number;
  offset: number;
};
export type StoreSubscriptionPaymentProofUrlResponse = {
  url: string | null;
};
export type StoreSubscriptionStatusItem = {
  storeKind?: StoreKind;
  tier?: "FREE" | "PREMIUM" | "COMPLIMENTARY" | "LEGACY";
  revision?: number;
  storeId: string;
  storeName: string;
  storeSlug: string;
  logoUrl: string | null;
  currency: string;
  planNameAr: string;
  planNameEn: string | null;
  type: SubscriptionPlanType;
  amount: number;
  /** The store's wallet balance. Named `creditBalance` on the wire. */
  creditBalance: number;
  periodDays: number;
  startDate: string;
  lastPaymentAt: string | null;
  nextPaymentDate: string | null;
  isOverdue: boolean;
  isPartiallyPaid: boolean;
  isFreeTrial: boolean;
  isTrialExpired: boolean;
  isPremium: boolean;
  isRestricted: boolean;
};
/**
 * A store's Google Tag Manager governance — the one storefront setting a seller
 * can save that executes code in every shopper's browser.
 *
 * `disabled` is a per-store kill switch, independent of `containerId`: it means
 * "this store may not run a container", so a seller re-saving the same id does
 * not revive it.
 *
 * `revalidated` reports whether the buyer cache was busted synchronously. False
 * means the write landed but the storefront may keep serving the container for
 * up to two more minutes — which the card must say rather than implying the
 * change is already live.
 */
export type AdminTagManagerView = {
  containerId: string | null;
  setAt: string | null;
  setByName: string | null;
  disabled: boolean;
  disabledAt: string | null;
  disabledByName: string | null;
  disabledReason: string | null;
  revalidated: boolean;
};

export type StorePremiumView = {
  storeId: string;
  isPremium: boolean;
  premiumSetAt: string | null;
};
export type StoreSubscriptionListResponse = {
  items: StoreSubscriptionStatusItem[];
  total: number;
  limit: number;
  offset: number;
};
export type StoreSubscriptionCurrentScheduleView = {
  id: string;
  dueDate: string;
  amountDue: number;
  paidAmount: number;
  currency: string;
  status: StoreChargeStatus;
  remainingAmount: number;
  /** The store's wallet balance. Named `creditBalance` on the wire. */
  creditBalance: number;
};
export type StoreSubscriptionPaymentAllocationView = {
  id: string;
  storeId: string;
  assignmentId: string | null;
  paymentId: string;
  paymentPaidAt: string | null;
  scheduleId: string | null;
  kind: StoreSubscriptionAllocationKind;
  amount: number;
  remainingAmount: number;
  deletedAt: string | null;
  createdAt: string;
};
export type StoreSubscriptionScheduleLedgerItemView = {
  schedule: StoreChargeLedgerView;
  allocations: StoreSubscriptionPaymentAllocationView[];
};
export type StoreSubscriptionLedgerResponse = {
  schedules: StoreSubscriptionScheduleLedgerItemView[];
  creditAllocations: StoreSubscriptionPaymentAllocationView[];
  /** The store's wallet balance. Named `creditBalance` on the wire. */
  creditBalance: number;
  currency: string;
};
export type StoreChargeView = {
  id: string;
  storeId: string;
  assignmentId: string;
  source: StoreChargeSource;
  /** Order id for ORDER charges, null for billing periods. */
  sourceRefId: string | null;
  dueDate: string;
  amountDue: number;
  paidAmount: number;
  currency: string;
  status: StoreChargeStatus;
  paidAt: string | null;
  createdAt: string;
  updatedAt: string;
};
/**
 * A charge as the ledger renders it. `sourceRefId` is an opaque uuid — printing
 * a truncated one ("Order ref: 019e93dc") matches nothing an operator can
 * search. `orderCode` is the human identifier (`TRIOTY-2606-XXE9TL`); null on
 * billing periods and on an ORDER charge whose order row was deleted.
 */
export type StoreChargeLedgerView = StoreChargeView & {
  orderCode: string | null;
};

export const STORE_SUBSCRIPTION_ALLOCATION_KIND = {
  SCHEDULE: "SCHEDULE",
  CREDIT: "CREDIT",
  /** A payment taken back: negative remaining amount, never spent. */
  REVERSAL: "REVERSAL",
} as const;

export type StoreSubscriptionAllocationKind =
  (typeof STORE_SUBSCRIPTION_ALLOCATION_KIND)[keyof typeof STORE_SUBSCRIPTION_ALLOCATION_KIND];

export const STORE_CHARGE_SOURCE = {
  SUBSCRIPTION_PERIOD: "SUBSCRIPTION_PERIOD",
  ORDER: "ORDER",
} as const;

export type StoreChargeSource =
  (typeof STORE_CHARGE_SOURCE)[keyof typeof STORE_CHARGE_SOURCE];

export const STORE_CHARGE_STATUS = {
  PENDING: "PENDING",
  PARTIAL: "PARTIAL",
  PAID: "PAID",
  /** Died before it was ever settled — nothing delivered, so nothing is owed. */
  VOID: "VOID",
  /** Closed by an admin, e.g. on leaving the per-order plan. */
  WAIVED: "WAIVED",
} as const;

export type StoreChargeStatus =
  (typeof STORE_CHARGE_STATUS)[keyof typeof STORE_CHARGE_STATUS];

export type SellerWithStoresResponse = {
  seller: SellerView;
  stores: StoreView[];
};
export type SellerInviteResponse = {
  inviteToken: string;
  expiresAt: string;
};
export type CreateSellerResponse = {
  seller: SellerView;
  store: StoreView;
  temporaryPassword: string;
};
export type CreateStoreResponse = {
  seller: SellerView;
  store: StoreView;
};
export type PaymentMethod = "COD" | "EXTERNAL_ONLINE";
export type OrderStatus =
  | "draft"
  | "paid_less_than_required"
  | "pending_buyer_verification"
  | "pending_buyer_payment"
  | "pending_payment_verification"
  | "confirmed"
  | "preparing"
  | "shipped"
  | "delivered"
  | "cancelled"
  | "declined";

export type DeclineReasonCode = "amount_not_received" | "amount_not_match";
export type PaymentReviewRejectReasonCode =
  | "amount_mismatch"
  | "proof_unclear"
  | "wrong_proof"
  | "suspected_fake"
  | "other";
export type ApproveOrderPaymentRequest = {
  approvedAmount: number;
  note?: string;
};
export type RejectOrderPaymentRequest = {
  reasonCode: PaymentReviewRejectReasonCode;
  note?: string;
};
export type OnlinePaymentProvider =
  | "paymob"
  | "paytabs"
  | "kashier"
  | "fawry"
  | "stripe";
export type OnlinePaymentStatus =
  | "initiated"
  | "pending"
  | "paid"
  | "failed"
  | "refunded"
  | "cancelled"
  | "expired";
/** How an online order was paid — mirrors the backend SellerOrderPaymentView. */
export type AdminOrderPaymentView = {
  provider: OnlinePaymentProvider;
  status: OnlinePaymentStatus;
  amount: number;
  currency: string;
  providerTxnId: string | null;
  lastEventAt: string | null;
  createdAt: string;
};
export type AdminOrderPaymentResponse = {
  transaction: AdminOrderPaymentView | null;
};
export type RefundOrderRequest = {
  amount?: number;
};

export type StoreHighlightView = {
  id: string;
  storeId: string;
  textAr: string;
  textEn: string | null;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
};
export type OrderItemSelectedOption = {
  optionGroupId: string;
  optionValueId: string;
  group: {
    ar: string;
    en: string | null;
  };
  value: {
    ar: string;
    en: string | null;
  };
};
export type OrderItemView = {
  productId: string;
  productVariantId: string;
  productNameAr: string;
  productNameEn: string | null;
  productSlug: string;
  resolvedTitleAr: string;
  resolvedTitleEn: string | null;
  selectedOptions: OrderItemSelectedOption[];
  quantity: number;
  unitPrice: number;
  unitOriginalPrice: number | null;
};
export type SellerOrderCustomer = {
  id: string;
  name: string;
  phoneE164: string;
};

// Structured delivery-address snapshot. `null` for legacy orders created
// before structured addresses — those fall back to the composed
// `buyerAddress` string. Mirrors the backend `OrderAddressView`.
export type OrderAddressView = {
  area: string;
  street: string;
  building: string | null;
  floor: string | null;
  apartment: string | null;
  landmark: string | null;
};

export type SellerOrderListItem = {
  id: string;
  orderCode: string;
  customer: SellerOrderCustomer;
  buyerAddress: string;
  address: OrderAddressView | null;
  paymentMethod: PaymentMethod;
  status: OrderStatus;
  statusEtaDays: number | null;
  declineReasonCode: DeclineReasonCode | null;
  declineReasonNote: string | null;
  paidAmount: number;
  requiredAmount: number;
  totalAmount: number;
  shippingFee: number;
  couponCode: string | null;
  couponDiscountAmount: number;
  createdAt: string;
  items: OrderItemView[];
};
export type OrderPaymentProofView = {
  id: string;
  orderId: string;
  objectKey: string;
  paidAmount: number;
  uploadedAt: string;
};
export type SellerOrderDetail = SellerOrderListItem & {
  buyerNote: string | null;
  paymentDetailId: string | null;
  depositPercent: number | null;
  updatedAt: string;
  items: OrderItemView[];
  paymentProofs: OrderPaymentProofView[];
  statusHistory: OrderStatusHistoryView[];
};
export type OrderStatusHistoryView = {
  id: string;
  orderId: string;
  oldStatus: OrderStatus;
  newStatus: OrderStatus;
  statusEtaDays: number | null;
  changedByType: "buyer" | "seller" | "admin";
  changedById: string | null;
  declineReasonCode: DeclineReasonCode | null;
  declineReasonNote: string | null;
  note: string | null;
  changedAt: string;
};
export type OrderPaymentProofUrlItem = {
  proofId: string;
  objectKey: string;
  paidAmount: number;
  uploadedAt: string;
  url: string;
};
export type OrderPaymentProofUrlResponse = {
  items: OrderPaymentProofUrlItem[];
};

export const INTEGRATION_LOG_DIRECTION = {
  OUTBOUND: "outbound",
  INBOUND: "inbound",
} as const;

export type IntegrationLogDirection =
  (typeof INTEGRATION_LOG_DIRECTION)[keyof typeof INTEGRATION_LOG_DIRECTION];

export const INTEGRATION_LOG_STATUS = {
  SUCCESS: "success",
  FAILED: "failed",
  PENDING: "pending",
} as const;

export type IntegrationLogStatus =
  (typeof INTEGRATION_LOG_STATUS)[keyof typeof INTEGRATION_LOG_STATUS];

/**
 * One row of the external-request audit log (`GET /admin/integration-logs`).
 * Every optional-looking field is nullable rather than absent because the
 * backend emits the full shape for both directions (an inbound webhook has no
 * `durationMs`, an unmatched request has no `storeId`, etc.).
 */
export type IntegrationLogListItem = {
  id: string;
  occurredAt: string;
  provider: string;
  direction: IntegrationLogDirection;
  status: IntegrationLogStatus;
  operation: string | null;
  method: string | null;
  url: string | null;
  httpStatus: number | null;
  durationMs: number | null;
  storeId: string | null;
  storeName: string | null;
  entityType: string | null;
  entityId: string | null;
  reference: string | null;
  correlationId: string | null;
  error: string | null;
};

export type IntegrationLogListResponse = {
  items: IntegrationLogListItem[];
  total: number;
  limit: number;
  offset: number;
};

/** JSON payload as stored by the backend — already redacted server-side. */
export type IntegrationLogPayload =
  | string
  | number
  | boolean
  | null
  | IntegrationLogPayload[]
  | { [key: string]: IntegrationLogPayload };

export type IntegrationLogDetail = IntegrationLogListItem & {
  requestPayload: IntegrationLogPayload;
  responsePayload: IntegrationLogPayload;
};

export type StoreSubscriptionStatusView = {
  tier: "FREE" | "PREMIUM" | "COMPLIMENTARY" | "LEGACY";
  revision: number;
  paidPeriodEndsAt: string | null;
  cancelAtPeriodEnd: boolean;
  planNameAr: string;
  planNameEn: string | null;
  wallet: { balance: number; currency: string };
  /** Admin status only: every lifecycle decision, newest first. */
  lifecycleHistory?: StoreSubscriptionHistoryEntry[];
};
/** One DEFAULT-store lifecycle decision (audit row) in the admin history. */
export type StoreSubscriptionHistoryEntry = {
  action: string;
  revision: number;
  occurredAt: string;
  admin: { id: string; name: string } | null;
  plan: {
    nameAr: string;
    nameEn: string | null;
    amount: number;
    currency: string;
  } | null;
};
export type StoreActivationQuote = {
  plan: SubscriptionPlanView;
  walletBalance: number;
  historicalDebtAmount: number;
  requiredAmount: number;
  canActivate: boolean;
  revision: number;
};
